'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Users, Shield, Calendar, Lock, Plus, Search, Edit2, Trash2, CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp, Percent, Settings, Building2, UserCheck, Eye, EyeOff, Save, RefreshCw } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface User {
  id: string; name: string; email: string; role: string;
  county: string; status: 'active' | 'suspended' | 'pending';
  lastLogin: string; permissions: string[];
}
interface Role {
  id: string; name: string; description: string;
  userCount: number; permissions: string[]; color: string;
}
interface TaxRate {
  id: string; category: string; rate: number; penalty: number;
  graceDays: number; zone: string; lastUpdated: string;
}
interface FiscalYear {
  id: string; year: string; startDate: string; endDate: string;
  status: 'active' | 'closed' | 'upcoming'; totalBudget: string; collected: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const USERS: User[] = [
  { id: 'USR-001', name: 'Commissioner James Kollie', email: 'j.kollie@lra.gov.lr', role: 'Commissioner', county: 'Montserrado', status: 'active', lastLogin: '2025-05-28 08:14', permissions: ['full_access', 'approve_filings', 'manage_users'] },
  { id: 'USR-002', name: 'Auditor Mary Pewee', email: 'm.pewee@lra.gov.lr', role: 'Auditor', county: 'Bong', status: 'active', lastLogin: '2025-05-27 14:32', permissions: ['view_filings', 'audit_cases', 'generate_reports'] },
  { id: 'USR-003', name: 'Field Officer Samuel Doe', email: 's.doe@lra.gov.lr', role: 'Field Officer', county: 'Nimba', status: 'active', lastLogin: '2025-05-28 07:50', permissions: ['field_inspections', 'update_parcels', 'view_registry'] },
  { id: 'USR-004', name: 'Auditor Fatima Kamara', email: 'f.kamara@lra.gov.lr', role: 'Auditor', county: 'Lofa', status: 'suspended', lastLogin: '2025-05-10 11:00', permissions: ['view_filings', 'audit_cases'] },
  { id: 'USR-005', name: 'Field Officer David Weah', email: 'd.weah@lra.gov.lr', role: 'Field Officer', county: 'Grand Bassa', status: 'pending', lastLogin: 'Never', permissions: ['field_inspections'] },
  { id: 'USR-006', name: 'Commissioner Agnes Flomo', email: 'a.flomo@lra.gov.lr', role: 'Commissioner', county: 'Margibi', status: 'active', lastLogin: '2025-05-28 09:01', permissions: ['full_access', 'approve_filings', 'manage_users', 'configure_rates'] },
];

const ROLES: Role[] = [
  { id: 'ROLE-1', name: 'Commissioner', description: 'Full system access — approve filings, manage users, configure rates, and view all county data.', userCount: 4, color: '#d4a017', permissions: ['full_access', 'approve_filings', 'manage_users', 'configure_rates', 'view_all_counties', 'generate_reports', 'audit_cases', 'field_inspections', 'update_parcels', 'view_registry', 'view_filings'] },
  { id: 'ROLE-2', name: 'Auditor', description: 'Read and audit access — review filings, run audit cases, and generate compliance reports.', userCount: 12, color: '#60a5fa', permissions: ['view_filings', 'audit_cases', 'generate_reports', 'view_registry'] },
  { id: 'ROLE-3', name: 'Field Officer', description: 'Field operations — conduct property inspections, update parcel status, and capture geo-tagged data.', userCount: 38, color: '#34d399', permissions: ['field_inspections', 'update_parcels', 'view_registry'] },
  { id: 'ROLE-4', name: 'Data Entry Clerk', description: 'Limited data entry — input taxpayer records and basic filing data under supervision.', userCount: 21, color: '#a78bfa', permissions: ['view_registry', 'view_filings'] },
];

const TAX_RATES: TaxRate[] = [
  // ── DEVELOPED LAND ──────────────────────────────────────────────────────────
  { id: 'TR-001', category: 'Commercial Building', rate: 1.5, penalty: 10.0, graceDays: 30, zone: 'Developed Land', lastUpdated: '2025-01-15' },
  { id: 'TR-002', category: 'Industrial Building', rate: 1.5, penalty: 10.0, graceDays: 30, zone: 'Developed Land', lastUpdated: '2025-01-15' },
  { id: 'TR-003', category: 'Residential Building', rate: 0.25, penalty: 10.0, graceDays: 30, zone: 'Developed Land', lastUpdated: '2025-01-15' },
  { id: 'TR-004', category: 'Farm in Urban Areas', rate: 0.33, penalty: 10.0, graceDays: 30, zone: 'Developed Land', lastUpdated: '2025-01-15' },
  { id: 'TR-005', category: 'Farm outside Urban Areas', rate: 0.25, penalty: 10.0, graceDays: 30, zone: 'Developed Land', lastUpdated: '2025-01-15' },
  { id: 'TR-006', category: 'Bldg on Public Land – Commercial', rate: 1.0, penalty: 10.0, graceDays: 30, zone: 'Developed (Public)', lastUpdated: '2025-01-15' },
  { id: 'TR-007', category: 'Bldg on Public Land – Residential', rate: 0.14, penalty: 10.0, graceDays: 30, zone: 'Developed (Public)', lastUpdated: '2025-01-15' },
  // ── UNDEVELOPED LAND ─────────────────────────────────────────────────────────
  { id: 'TR-008', category: 'City/Town Lots (undeveloped)', rate: 3.5, penalty: 10.0, graceDays: 30, zone: 'Undeveloped – City/Town', lastUpdated: '2025-01-15' },
  { id: 'TR-009', category: 'Farmland (undeveloped, within city/town)', rate: 4.0, penalty: 10.0, graceDays: 30, zone: 'Undeveloped – City/Town', lastUpdated: '2025-01-15' },
  { id: 'TR-010', category: 'Acre or Above – Vacant Land (within city/town)', rate: 4.5, penalty: 10.0, graceDays: 30, zone: 'Undeveloped – City/Town', lastUpdated: '2025-01-15' },
  { id: 'TR-011', category: 'All Land without Building (outside city/town)', rate: 0, penalty: 10.0, graceDays: 30, zone: 'Undeveloped – Outside City', lastUpdated: '2025-01-15' },
];

const FISCAL_YEARS: FiscalYear[] = [
  { id: 'FY-2025', year: '2025', startDate: '2025-01-01', endDate: '2025-12-31', status: 'active', totalBudget: '$485M', collected: '$312M' },
  { id: 'FY-2024', year: '2024', startDate: '2024-01-01', endDate: '2024-12-31', status: 'closed', totalBudget: '$420M', collected: '$398M' },
  { id: 'FY-2023', year: '2023', startDate: '2023-01-01', endDate: '2023-12-31', status: 'closed', totalBudget: '$380M', collected: '$361M' },
  { id: 'FY-2026', year: '2026', startDate: '2026-01-01', endDate: '2026-12-31', status: 'upcoming', totalBudget: '$530M', collected: '$0' },
];

const ALL_PERMISSIONS = [
  { key: 'full_access', label: 'Full System Access', group: 'System' },
  { key: 'manage_users', label: 'Manage Users & Roles', group: 'System' },
  { key: 'configure_rates', label: 'Configure Tax Rates', group: 'System' },
  { key: 'view_all_counties', label: 'View All Counties', group: 'System' },
  { key: 'approve_filings', label: 'Approve Tax Filings', group: 'Filings' },
  { key: 'view_filings', label: 'View Tax Filings', group: 'Filings' },
  { key: 'audit_cases', label: 'Manage Audit Cases', group: 'Audit' },
  { key: 'generate_reports', label: 'Generate Reports', group: 'Audit' },
  { key: 'field_inspections', label: 'Conduct Field Inspections', group: 'Field' },
  { key: 'update_parcels', label: 'Update Parcel Records', group: 'Field' },
  { key: 'view_registry', label: 'View Taxpayer Registry', group: 'Registry' },
];

const TABS = [
  { id: 'users', label: 'User & Role Management', icon: Users },
  { id: 'rates', label: 'Tax Rate & Penalty Config', icon: Percent },
  { id: 'fiscal', label: 'Fiscal Year Settings', icon: Calendar },
  { id: 'matrix', label: 'Permission Matrix', icon: Lock },
];

const statusStyle: Record<string, { color: string; bg: string }> = {
  active: { color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  suspended: { color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  pending: { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  closed: { color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  upcoming: { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
};

// ─── Sub-panels ───────────────────────────────────────────────────────────────
function UserRolePanel() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [view, setView] = useState<'users' | 'roles'>('users');
  const [expandedRole, setExpandedRole] = useState<string | null>(null);

  const filtered = USERS.filter((u) => {
    const matchSearch = !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'All' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  return (
    <div>
      {/* Sub-tab toggle */}
      <div className="flex gap-2 mb-5">
        {(['users', 'roles'] as const).map((v) => (
          <button key={v} onClick={() => setView(v)}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: view === v ? 'rgba(212,160,23,0.18)' : 'var(--dark-card)',
              color: view === v ? 'var(--accent)' : 'var(--dark-muted)',
              border: `1px solid ${view === v ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
            }}>
            {v === 'users' ? 'Users' : 'Roles'}
          </button>
        ))}
      </div>

      {view === 'users' ? (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users…"
                className="w-full pl-8 pr-3 py-2 rounded-lg text-sm outline-none"
                style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }} />
            </div>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm outline-none"
              style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}>
              <option>All</option>
              {ROLES.map((r) => <option key={r.id}>{r.name}</option>)}
            </select>
            <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
              style={{ background: 'linear-gradient(135deg,#b8860b,#d4a017)', color: '#070e1f' }}>
              <Plus size={14} /> Add User
            </button>
          </div>

          {/* Table */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--dark-elevated)' }}>
                  {['User', 'Role', 'County', 'Status', 'Last Login', 'Actions'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider"
                      style={{ color: 'var(--dark-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, i) => {
                  const st = statusStyle[u.status];
                  return (
                    <tr key={u.id} style={{ backgroundColor: i % 2 === 0 ? 'var(--dark-card)' : 'var(--dark-surface)', borderTop: '1px solid var(--dark-border)' }}>
                      <td className="px-4 py-3">
                        <div className="font-medium" style={{ color: 'var(--dark-text)' }}>{u.name}</div>
                        <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: 'rgba(212,160,23,0.12)', color: 'var(--accent)' }}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--dark-muted)' }}>{u.county}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full capitalize"
                          style={{ backgroundColor: st.bg, color: st.color }}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--dark-muted)' }}>{u.lastLogin}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title="Edit">
                            <Edit2 size={13} style={{ color: 'var(--accent)' }} />
                          </button>
                          <button className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title="Toggle status">
                            {u.status === 'active' ? <EyeOff size={13} style={{ color: '#f87171' }} /> : <Eye size={13} style={{ color: '#34d399' }} />}
                          </button>
                          <button className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title="Remove">
                            <Trash2 size={13} style={{ color: '#f87171' }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ROLES.map((role) => (
            <div key={role.id} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: role.color }} />
                  <span className="font-semibold text-sm" style={{ color: 'var(--dark-text)' }}>{role.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(212,160,23,0.12)', color: 'var(--accent)' }}>
                    {role.userCount} users
                  </span>
                  <button onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
                    className="p-1 rounded hover:bg-white/5 transition-colors">
                    {expandedRole === role.id ? <ChevronUp size={14} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--dark-muted)' }} />}
                  </button>
                </div>
              </div>
              <p className="text-xs mb-3" style={{ color: 'var(--dark-muted)' }}>{role.description}</p>
              {expandedRole === role.id && (
                <div className="flex flex-wrap gap-1.5 mt-2 pt-3" style={{ borderTop: '1px solid var(--dark-border)' }}>
                  {role.permissions.map((p) => {
                    const perm = ALL_PERMISSIONS.find((ap) => ap.key === p);
                    return (
                      <span key={p} className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: 'rgba(96,165,250,0.1)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)' }}>
                        {perm?.label ?? p}
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <button className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
                  style={{ backgroundColor: 'rgba(212,160,23,0.1)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.2)' }}>
                  <Edit2 size={11} /> Edit Role
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaxRatePanel() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rates, setRates] = useState(TAX_RATES);

  const zoneColor: Record<string, string> = {
    Urban: '#60a5fa', Rural: '#34d399', Industrial: '#f87171', 'All Zones': '#a78bfa',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
          Configure property tax rates, penalty percentages, and grace periods per land-use category.
        </p>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
          style={{ background: 'linear-gradient(135deg,#b8860b,#d4a017)', color: '#070e1f' }}>
          <Plus size={14} /> Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {rates.map((rate) => (
          <div key={rate.id} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[160px]">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-sm" style={{ color: 'var(--dark-text)' }}>{rate.category}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${zoneColor[rate.zone] ?? '#94a3b8'}18`, color: zoneColor[rate.zone] ?? '#94a3b8' }}>
                    {rate.zone}
                  </span>
                </div>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Last updated: {rate.lastUpdated}</span>
              </div>

              {editingId === rate.id ? (
                <>
                  <div className="flex items-center gap-1">
                    <label className="text-xs" style={{ color: 'var(--dark-muted)' }}>Rate %</label>
                    <input type="number" defaultValue={rate.rate} step="0.1"
                      className="w-16 px-2 py-1 rounded text-sm text-center outline-none"
                      style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }} />
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs" style={{ color: 'var(--dark-muted)' }}>Penalty %</label>
                    <input type="number" defaultValue={rate.penalty} step="0.5"
                      className="w-16 px-2 py-1 rounded text-sm text-center outline-none"
                      style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }} />
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs" style={{ color: 'var(--dark-muted)' }}>Grace Days</label>
                    <input type="number" defaultValue={rate.graceDays}
                      className="w-16 px-2 py-1 rounded text-sm text-center outline-none"
                      style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }} />
                  </div>
                  <button onClick={() => setEditingId(null)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ background: 'linear-gradient(135deg,#b8860b,#d4a017)', color: '#070e1f' }}>
                    <Save size={12} /> Save
                  </button>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <div className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{rate.rate}%</div>
                    <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Tax Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold" style={{ color: '#f87171' }}>{rate.penalty}%</div>
                    <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Penalty</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold" style={{ color: '#60a5fa' }}>{rate.graceDays}d</div>
                    <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Grace Period</div>
                  </div>
                  <button onClick={() => setEditingId(rate.id)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs"
                    style={{ backgroundColor: 'rgba(212,160,23,0.1)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.2)' }}>
                    <Edit2 size={11} /> Edit
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* LRA Rate Source Note */}
      <div className="mt-4 rounded-xl p-4 flex items-start gap-3"
        style={{ backgroundColor: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)' }}>
        <Settings size={16} style={{ color: '#60a5fa', flexShrink: 0, marginTop: 2 }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: '#60a5fa' }}>Official LRA Real Property Tax Rates</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
            Rates above reflect the official Liberia Revenue Authority Real Property Tax Schedule. Developed Land: Commercial 1.5%, Industrial 1.5%, Residential 0.25%, Farm (urban) 0.33%, Farm (outside urban) 0.25%, Public Land Commercial 1%, Public Land Residential 0.14%. Undeveloped Land: City/Town Lots 3.5%, Farmland 4%, Vacant Acre+ 4.5%, Outside City/Town LRD $5.00/acre. Source: revenue.lra.gov.lr/real-property-taxes/
          </p>
        </div>
      </div>

      {/* Penalty escalation note */}
      <div className="mt-3 rounded-xl p-4 flex items-start gap-3"
        style={{ backgroundColor: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)' }}>
        <AlertTriangle size={16} style={{ color: '#fbbf24', flexShrink: 0, marginTop: 2 }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: '#fbbf24' }}>LRA Penalty Policy (Section 51)</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
            Late filing or failure to file: 10% penalty, increased by 5% monthly. Taxes are levied July 1st of each year. Accounts exceeding 90 days are flagged for lien proceedings. TIN required for all payments. Under-declaration subject to additional assessment by LRA.
          </p>
        </div>
      </div>
    </div>
  );
}

function FiscalYearPanel() {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
          Manage fiscal year cycles, budget targets, and collection milestones.
        </p>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
          style={{ background: 'linear-gradient(135deg,#b8860b,#d4a017)', color: '#070e1f' }}>
          <Plus size={14} /> New Fiscal Year
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {FISCAL_YEARS.map((fy) => {
          const st = statusStyle[fy.status];
          const isActive = fy.status === 'active';
          const progress = fy.status !== 'upcoming'
            ? Math.round((parseFloat(fy.collected.replace(/[$M]/g, '')) / parseFloat(fy.totalBudget.replace(/[$M]/g, ''))) * 100)
            : 0;
          return (
            <div key={fy.id}
              onClick={() => setSelected(selected === fy.id ? null : fy.id)}
              className="rounded-xl p-4 cursor-pointer transition-all"
              style={{
                backgroundColor: 'var(--dark-card)',
                border: `1px solid ${selected === fy.id ? 'rgba(212,160,23,0.5)' : 'var(--dark-border)'}`,
                boxShadow: isActive ? '0 0 0 1px rgba(212,160,23,0.2)' : 'none',
              }}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold" style={{ color: 'var(--dark-text)' }}>FY {fy.year}</span>
                    {isActive && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399' }}>● ACTIVE</span>
                    )}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{fy.startDate} → {fy.endDate}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full capitalize font-semibold"
                  style={{ backgroundColor: st.bg, color: st.color }}>{fy.status}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Budget Target</p>
                  <p className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>{fy.totalBudget}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Collected</p>
                  <p className="text-base font-bold" style={{ color: '#34d399' }}>{fy.collected}</p>
                </div>
              </div>
              {fy.status !== 'upcoming' && (
                <div>
                  <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>
                    <span>Collection Progress</span><span style={{ color: 'var(--accent)' }}>{progress}%</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${progress}%`, background: 'linear-gradient(90deg,#b8860b,#d4a017)' }} />
                  </div>
                </div>
              )}
              {selected === fy.id && (
                <div className="mt-4 pt-3 flex gap-2" style={{ borderTop: '1px solid var(--dark-border)' }}>
                  {fy.status === 'upcoming' && (
                    <button className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
                      style={{ background: 'linear-gradient(135deg,#b8860b,#d4a017)', color: '#070e1f' }}>
                      <CheckCircle size={11} /> Activate
                    </button>
                  )}
                  {fy.status === 'active' && (
                    <button className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
                      style={{ backgroundColor: 'rgba(248,113,113,0.1)', color: '#f87171', border: '1px solid rgba(248,113,113,0.2)' }}>
                      <XCircle size={11} /> Close Year
                    </button>
                  )}
                  <button className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
                    style={{ backgroundColor: 'rgba(212,160,23,0.1)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.2)' }}>
                    <Edit2 size={11} /> Edit Settings
                  </button>
                  <button className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
                    style={{ backgroundColor: 'rgba(96,165,250,0.1)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)' }}>
                    <RefreshCw size={11} /> Sync Data
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Global fiscal settings */}
      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--dark-text)' }}>
          <Settings size={14} style={{ color: 'var(--accent)' }} /> Global Fiscal Settings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Fiscal Year Start Month', value: 'January', type: 'select', options: ['January', 'April', 'July', 'October'] },
            { label: 'Currency', value: 'LRD (Liberian Dollar)', type: 'select', options: ['LRD (Liberian Dollar)', 'USD (US Dollar)'] },
            { label: 'Reporting Frequency', value: 'Monthly', type: 'select', options: ['Monthly', 'Quarterly', 'Annually'] },
          ].map((s) => (
            <div key={s.label}>
              <label className="text-xs font-medium block mb-1" style={{ color: 'var(--dark-muted)' }}>{s.label}</label>
              <select className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}>
                {s.options.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
            style={{ background: 'linear-gradient(135deg,#b8860b,#d4a017)', color: '#070e1f' }}>
            <Save size={13} /> Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}

function PermissionMatrixPanel() {
  const MATRIX_ROLES = ['Commissioner', 'Auditor', 'Field Officer', 'Data Entry Clerk'];
  const permGroups = ['System', 'Filings', 'Audit', 'Field', 'Registry'];

  const rolePermissions: Record<string, string[]> = {
    Commissioner: ROLES[0].permissions,
    Auditor: ROLES[1].permissions,
    'Field Officer': ROLES[2].permissions,
    'Data Entry Clerk': ROLES[3].permissions,
  };

  const roleColors: Record<string, string> = {
    Commissioner: '#d4a017', Auditor: '#60a5fa', 'Field Officer': '#34d399', 'Data Entry Clerk': '#a78bfa',
  };

  return (
    <div>
      <p className="text-sm mb-5" style={{ color: 'var(--dark-muted)' }}>
        System-wide permission matrix — view and manage what each role can access across all modules.
      </p>

      <div className="rounded-xl overflow-auto" style={{ border: '1px solid var(--dark-border)' }}>
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr style={{ backgroundColor: 'var(--dark-elevated)' }}>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider w-48"
                style={{ color: 'var(--dark-muted)' }}>Permission</th>
              {MATRIX_ROLES.map((r) => (
                <th key={r} className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider"
                  style={{ color: roleColors[r] }}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {permGroups.map((group) => {
              const groupPerms = ALL_PERMISSIONS.filter((p) => p.group === group);
              return (
                <React.Fragment key={group}>
                  <tr style={{ backgroundColor: 'rgba(212,160,23,0.04)' }}>
                    <td colSpan={5} className="px-4 py-2 text-xs font-bold uppercase tracking-widest"
                      style={{ color: 'var(--accent)', borderTop: '1px solid var(--dark-border)' }}>
                      {group}
                    </td>
                  </tr>
                  {groupPerms.map((perm, i) => (
                    <tr key={perm.key}
                      style={{ backgroundColor: i % 2 === 0 ? 'var(--dark-card)' : 'var(--dark-surface)', borderTop: '1px solid var(--dark-border)' }}>
                      <td className="px-4 py-2.5 text-sm" style={{ color: 'var(--dark-text)' }}>{perm.label}</td>
                      {MATRIX_ROLES.map((role) => {
                        const has = rolePermissions[role]?.includes(perm.key);
                        return (
                          <td key={role} className="px-3 py-2.5 text-center">
                            {has ? (
                              <CheckCircle size={16} className="mx-auto" style={{ color: '#34d399' }} />
                            ) : (
                              <XCircle size={16} className="mx-auto" style={{ color: 'rgba(148,163,184,0.3)' }} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-4">
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--dark-muted)' }}>
          <CheckCircle size={14} style={{ color: '#34d399' }} /> Permission granted
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--dark-muted)' }}>
          <XCircle size={14} style={{ color: 'rgba(148,163,184,0.3)' }} /> Not granted
        </div>
        {MATRIX_ROLES.map((r) => (
          <div key={r} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--dark-muted)' }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: roleColors[r] }} />
            {r}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EnterpriseAdminClient() {
  const [activeTab, setActiveTab] = useState('users');

  const kpis = [
    { label: 'Total Users', value: '75', icon: Users, color: 'var(--accent)' },
    { label: 'Active Roles', value: '4', icon: Shield, color: '#60a5fa' },
    { label: 'Active Fiscal Year', value: 'FY 2025', icon: Calendar, color: '#34d399' },
    { label: 'Permission Groups', value: '5', icon: Lock, color: '#a78bfa' },
  ];

  const renderPanel = () => {
    switch (activeTab) {
      case 'users': return <UserRolePanel />;
      case 'rates': return <TaxRatePanel />;
      case 'fiscal': return <FiscalYearPanel />;
      case 'matrix': return <PermissionMatrixPanel />;
      default: return null;
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#b8860b 0%,#d4a017 100%)' }}>
              <Building2 size={20} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--dark-text)' }}>Enterprise Admin Hub</h1>
              <p className="text-xs sm:text-sm" style={{ color: 'var(--dark-muted)' }}>
                RBAC · Tax Configuration · Fiscal Settings · Permission Matrix — system-wide governance
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)' }}>
            <UserCheck size={12} /> Admin Access
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {kpis.map((k) => {
            const KIcon = k.icon;
            return (
              <div key={k.label} className="rounded-xl px-4 py-4 flex items-center gap-3"
                style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${k.color}18` }}>
                  <KIcon size={18} style={{ color: k.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
                  <p className="text-lg font-bold leading-tight" style={{ color: 'var(--dark-text)' }}>{k.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-5 p-1 rounded-xl" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
          {TABS.map((tab) => {
            const TIcon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center"
                style={{
                  backgroundColor: active ? 'rgba(212,160,23,0.18)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--dark-muted)',
                  border: active ? '1px solid rgba(212,160,23,0.35)' : '1px solid transparent',
                }}>
                <TIcon size={14} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
          {renderPanel()}
        </div>
      </div>
    </AppLayout>
  );
}
